import { Component, DOCUMENT, Inject, Optional, PLATFORM_ID, REQUEST } from '@angular/core';
import {NavigationEnd, Router, RouterOutlet} from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AuthenticationService } from './core/services/authentication/authentication.service';
import { isPlatformBrowser } from '@angular/common';
import { SeoService } from './core/services/seo/seo.service';
import {UtmService} from './core/services/analytics/utm.service';
import {filter, take} from 'rxjs';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TranslocoPipe],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  private structuredDataInjected = false;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly transloco: TranslocoService,
    private readonly router: Router,
    private readonly seoService: SeoService,
    private readonly utmService: UtmService,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    @Optional() @Inject(REQUEST) private readonly request?: Request,
  ) {
    this.setLanguage();
    this.injectStructuredDataIfNeeded();
  }

  private setLanguage() {
    const availableLangs = this.transloco.getAvailableLangs() as string[];

    if (!isPlatformBrowser(this.platformId)) {
      const cookieLang = this.getCookieFromRequest('lang');
      const acceptLang = this.request?.headers?.get('accept-language') ?? '';
      const selectedLang =
        (cookieLang && availableLangs.includes(cookieLang) && cookieLang) ||
        this.pickLangFromAcceptLanguage(acceptLang, availableLangs) ||
        'en';

      this.transloco.setActiveLang(selectedLang);
      this.document.documentElement.lang = selectedLang;
      return;
    }

    // Capture on the FIRST completed navigation only
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      take(1)
    ).subscribe(e => {
      // Persist if URL contains any utm_* or clid params
      const hasMarkers = /[?&](utm_(source|medium|campaign|content|term)|gclid|fbclid)=/i.test(e.urlAfterRedirects);
      if (hasMarkers) {
        this.utmService.persistFromUrl(e.urlAfterRedirects);
      }
    });

    const cookieLang = this.getCookie('lang');
    const browserLang = navigator.language.split('-')[0]; // Extract 'en' from 'en-US'
    let selectedLang = 'en'; // Default fallback language

    if (cookieLang && availableLangs.includes(cookieLang)) {
      selectedLang = cookieLang;
    } else if (availableLangs.includes(browserLang)) {
      selectedLang = browserLang;
    }

    // Update the cookie with the valid language
    this.setCookie('lang', selectedLang, 365);

    this.transloco.setActiveLang(selectedLang);        // Set language in Transloco
    this.document.documentElement.lang = selectedLang; // Also reflect it in the <html lang> attribute for a11y/SEO/SSR hints
  }

  private getCookie(name: string): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    const cookies = document.cookie.split('; ');
    const cookie = cookies.find((row) => row.startsWith(`${name}=`));
    return cookie ? cookie.split('=')[1] : null;
  }

  private getCookieFromRequest(name: string): string | null {
    const raw = this.request?.headers?.get('cookie');
    if (!raw) return null;
    const match = raw.split('; ').find((row) => row.startsWith(`${name}=`));
    return match ? match.split('=')[1] : null;
  }

  private pickLangFromAcceptLanguage(header: string, availableLangs: string[]): string | null {
    if (!header) return null;
    const candidates = header
      .split(',')
      .map((part) => part.split(';')[0]?.trim().toLowerCase())
      .filter(Boolean);

    for (const candidate of candidates) {
      if (candidate.startsWith('fr') && availableLangs.includes('fr')) return 'fr';
      if (candidate.startsWith('en') && availableLangs.includes('en')) return 'en';
    }
    return null;
  }

  private setCookie(name: string, value: string, days: number) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const expires = new Date();
    expires.setDate(expires.getDate() + days);
    document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/`;
  }

  public isImpersonated(): boolean {
    return this.authenticationService.isImpersonated();
  }

  public logout(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.authenticationService.logout();
    window.location.href = '/login';
  }

  public restoreAdmin(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const backup = sessionStorage.getItem('admin_backup_token');
    if (backup) {
      this.authenticationService.saveTokens(JSON.parse(backup));
      sessionStorage.removeItem('admin_backup_token');
      window.location.href = '/admin/users';
    } else {
      this.logout();
    }
  }

  private injectStructuredDataIfNeeded(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.router.events.subscribe(() => {
      if (this.router.url === '/' && !this.structuredDataInjected) {
        this.seoService.injectStructuredDataForHomepage(this.document);
        this.structuredDataInjected = true;
      }
    });
  }
}
